import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import key from 'weak-key';

// components
import { SingleTile } from 'components/Product/Tile/SingleTile';
import { TilesView } from 'components/Product/Tile/TilesView';

// utils
import {
  categoriesByIdsSelector,
  currentCatalogSelector,
} from 'utils/selectors/productCatalogSelectors';
import { fetchProductsByCategory } from 'components/Product/actions';
import { isEmpty } from 'utils/is-empty';

import type { CategoryTilesProps } from './CategoryTiles.types';
import type { ProductCategory } from 'components/Product/reducer.types';

export function CategoryTiles({
  categoryItems,
  hideProductCounts = false,
  isSectionReference,
  ...props
}: Readonly<CategoryTilesProps>) {
  const dispatch = useDispatch();
  const categories = useSelector((state) => categoriesByIdsSelector(state, categoryItems));
  const catalog = useSelector(currentCatalogSelector);

  useEffect(() => {
    if (!isEmpty(categoryItems)) {
      categoryItems.forEach((categoryItem) => {
        dispatch(fetchProductsByCategory(categoryItem));
      });
    }
  }, [catalog, categoryItems, dispatch]);

  // error handling category tiles
  // don't show the tile when
  // no category or no picture or no products in it
  const filteredCategories = (categories || []).filter(
    (category): category is ProductCategory =>
      !isEmpty(category) &&
      category.isFetching === false &&
      (category.total ?? 0) > 0 &&
      !isEmpty(category.pictureObject) &&
      !isEmpty(category.pictureObject.url),
  );

  const renderTiles = () =>
    filteredCategories.map((category, index) => (
      <div
        className="c-product-tiles__column"
        key={key(category)}
        data-preview-id={!isSectionReference ? `#${index}` : undefined}
      >
        <SingleTile
          product={category}
          linkType="product_listing_page"
          key={key(category)}
          total={!hideProductCounts ? category.total : 0}
          categoryName={category.name}
          isCategoryPage
        />
      </div>
    ));

  if (isEmpty(filteredCategories)) {
    return null;
  }

  return (
    <TilesView<ProductCategory>
      componentName="subcategory-tiles"
      items={filteredCategories}
      renderTiles={renderTiles}
      {...props}
    />
  );
}
